import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import { useConsents } from "../hooks/useConsents";
import DisabledFeature from "./DisabledFeature";
import toast from "react-hot-toast";
import { trackEvent } from "../utils/trackEvent";
import "../styles/ResumesManager.css";

interface Resume {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  isPrimary: boolean;
}

const ResumesManager: React.FC = () => {
  const navigate = useNavigate();
  const { consents, loading: consentsLoading } = useConsents();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/auth/resumes");
      setResumes(response.data.resumes);
    } catch (error: any) {
      toast.error("Failed to load resumes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("resume", file);

      await axiosInstance.post("/auth/resumes/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      trackEvent("resume_uploaded", { source: "resumes_manager" });
      toast.success("Resume uploaded successfully");
      fetchResumes();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to upload resume");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (resumeId: string) => {
    try {
      await axiosInstance.patch(`/auth/resumes/${resumeId}/set-primary`);
      toast.success("Primary resume updated");
      fetchResumes();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to set primary resume");
    }
  };

  const handleDelete = async (resumeId: string) => {
    if (!window.confirm("Are you sure you want to delete this resume?")) return;

    try {
      await axiosInstance.delete(`/auth/resumes/${resumeId}`);
      toast.success("Resume deleted");
      fetchResumes();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to delete resume");
    }
  };

  // Open the resume in a new tab. We fetch as a blob through axiosInstance so
  // the bearer token + CSRF interceptor are applied, then hand the browser a
  // temporary blob URL for inline preview/download.
  const handleView = async (resumeId: string, fileName: string) => {
    try {
      const response = await axiosInstance.get(`/auth/resumes/${resumeId}/file`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(response.data);
      const win = window.open(blobUrl, "_blank");
      if (!win) {
        // Popup blocked: fall back to a download anchor.
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke after a delay so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to open resume");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Check if data_storage consent is granted
  if (!consentsLoading && !consents.data_storage) {
    return (
      <div className="hb-resumes-container">
        <h2 className="hb-resumes-title">Resume Manager</h2>
        <DisabledFeature
          icon="📄"
          featureName="Resume Manager"
          consentType="data_storage"
          description="To store and manage your resumes, you need to grant data storage permission. Your resumes will be securely stored and used for job matching."
        />
      </div>
    );
  }

  return (
    <div className="hb-resumes-container">
      <button
        onClick={() => navigate("/dashboard")}
        className="hb-resumes-back-btn"
      >
        ← Back to Dashboard
      </button>

      <h2 className="hb-resumes-title">Resume Manager</h2>

      {/* Create Resume Section */}
      <div className="hb-resumes-create">
        <h3>Create a Resume</h3>
        <p>Build a professional resume from scratch with our easy-to-use builder</p>
        <button 
          onClick={() => navigate("/resume-builder")}
          className="hb-resumes-create-btn"
        >
          ✨ Create New Resume
        </button>
      </div>

      {/* Create Cover Letter Section */}
      <div className="hb-resumes-create hb-cover-letter-create">
        <h3>Create a Cover Letter</h3>
        <p>Craft a compelling cover letter tailored to your target position</p>
        <button 
          onClick={() => navigate("/cover-letter-builder")}
          className="hb-resumes-create-btn hb-cover-letter-btn"
        >
          📝 Create New Cover Letter
        </button>
      </div>

      {/* Upload Section */}
      <div className="hb-resumes-upload">
        <h3>Upload a Resume</h3>
        <p>Supported formats: PDF, DOC, DOCX, TXT</p>
        <input
          type="file"
          id="resume-upload"
          className="hb-resumes-upload-input"
          onChange={handleFileChange}
          disabled={uploading}
          accept=".pdf,.doc,.docx,.txt"
        />
        {uploading && <p className="hb-resumes-uploading">Uploading...</p>}
      </div>

      {/* Resumes List */}
      <div className="hb-resumes-list-section">
        <h3>Your Resumes ({resumes.length})</h3>

        {loading ? (
          <p className="hb-resumes-loading">Loading resumes...</p>
        ) : resumes.length === 0 ? (
          <p className="hb-resumes-empty">No resumes uploaded yet. Upload your first resume above!</p>
        ) : (
          <div className="hb-resumes-grid">
            {resumes.map((resume) => (
              <div
                key={resume.id}
                className={`hb-resume-card ${resume.isPrimary ? 'primary' : ''}`}
              >
                <h4 className="hb-resume-filename">
                  {resume.fileName}
                </h4>
                <p className="hb-resume-meta">
                  <strong>Size:</strong> {formatFileSize(resume.fileSize)}
                </p>
                <p className="hb-resume-meta">
                  <strong>Uploaded:</strong> {formatDate(resume.uploadedAt)}
                </p>

                {resume.isPrimary && (
                  <span className="hb-resume-primary-badge">
                    Primary Resume
                  </span>
                )}

                <div className="hb-resume-actions">
                  <button
                    onClick={() => handleView(resume.id, resume.fileName)}
                    className="hb-resume-btn hb-resume-btn-primary"
                  >
                    View
                  </button>
                  {!resume.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(resume.id)}
                      className="hb-resume-btn hb-resume-btn-primary"
                    >
                      Set as Primary
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(resume.id)}
                    className="hb-resume-btn hb-resume-btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default ResumesManager;
